import { gql } from "@apollo/client";

export const CARE_SETTINGS_FIELDS = `
  id
  businessId

  afhLicenseNumber
  afhLicenseVerified
  licenseStatus
  licenseExpiresOn
  licenseType
  licenseState
  facilityStatusCode
  verificationSource
  verifiedAt
  lastCheckedAt
  specialty

  acceptsMedicaid
  hasPrivateRooms
  hasSharedRooms
  acceptsLongTermCareInsurance
  acceptsRespiteCare
  acceptsVeteransBenefits
  acceptsDevelopmentalDisabilities
  acceptsWheelchairAccess
  has24HourCare
  hasRegisteredNurse
  hasVisitingPhysician
  acceptsBedboundResidents
  isPetFriendly
  basePriceMonthly
  genderPreference
  facilityIsCoEd
  privateBathroomAvailable
  hoyerLiftAvailable
  ownersStaffDescription
  specialFeaturesDescription
  philosophyValuesDescription
  dailyLifeDescription
  languagesSpoken
  religionDetails
  houseRules
  includedServicesDetails
  optionalServicesDetails
  mealsDetails
  hygieneDetails
  transportationDetails
  equipmentDetails
  petsAllowed
  catsAllowed
  dogsAllowed
  facilityHasPets
  allowsVapingTobacco
  allowsVapingMedicalMarijuana
  allowsEdibleMedicalMarijuana
  manualWheelchairAllowed
  powerWheelchairAllowed
  powerScooterAllowed

  careLevels { id name }
  referralSourceTypes { id name }
`;

export const CARE_META_FIELDS = `
  bedsOpen
  bedsTotal
  roomsOpen
  notes
  bedDetails {
    roomId
    roomName
    roomType
    bedId
    bedLabel
    isOccupied
    gender
    notes 
    hasPrivateBathroom
    hasHospitalBed
    hasHoyerLift
    hasOxygen
    accommodatesBariatric 
  }
`;
export const SAVE_CARE_SETTINGS = gql`
  mutation SaveCareSettings($input: CareSettingsInput!) {
    saveCareSettings(input: $input) {
      success
      message
      careSetting {
        id
        businessId
        afhLicenseNumber
        afhLicenseVerified
        specialty
        acceptsMedicaid
        hasPrivateRooms
        hasSharedRooms
        acceptsLongTermCareInsurance
        acceptsRespiteCare
        acceptsVeteransBenefits
        acceptsDevelopmentalDisabilities
        acceptsWheelchairAccess
        has24HourCare
        hasRegisteredNurse
        hasVisitingPhysician
        acceptsBedboundResidents
        isPetFriendly
        basePriceMonthly
        genderPreference
        facilityIsCoEd
        privateBathroomAvailable
        hoyerLiftAvailable
        ownersStaffDescription
        specialFeaturesDescription
        philosophyValuesDescription
        dailyLifeDescription
        languagesSpoken
        religionDetails
        houseRules
        includedServicesDetails
        optionalServicesDetails
        mealsDetails
        hygieneDetails
        transportationDetails
        equipmentDetails
        petsAllowed
        catsAllowed
        dogsAllowed
        facilityHasPets
        allowsVapingTobacco
        allowsVapingMedicalMarijuana
        allowsEdibleMedicalMarijuana
        manualWheelchairAllowed
        powerWheelchairAllowed
        powerScooterAllowed
        careLevels { id name }
        referralSourceTypes { id name }
      }
    }
  }
`;

export const UPSERT_ROOMS = gql`
  mutation UpsertRooms($businessId: Int!, $rooms: [RoomInput!]!) {
    upsertRooms(businessId: $businessId, rooms: $rooms) {
      id
      businessId
      roomNumber
      roomType 
      notes
      beds {
        id
        bedLabel
        gender
        isOccupied
        hasPrivateBathroom
        hasHospitalBed
        hasHoyerLift
        hasOxygen
        accommodatesBariatric
        notes
      }
    }
  }
`;

// Update Room
export const UPDATE_ROOM = gql`
  mutation UpdateRoom($id: ID!, $input: RoomInput!) {
    updateRoom(id: $id, input: $input) {
      id
      businessId
      roomNumber
      roomType
      notes
      beds {
        id
        bedLabel
        gender
        isOccupied
      }
    }
  }
`;

// Delete Room
export const DELETE_ROOM = gql`
  mutation DeleteRoom($id: ID!) {
    deleteRoom(id: $id)
  }
`;

// Create Bed
export const CREATE_BED = gql`
  mutation CreateBed($input: BedInput!) {
    createBed(input: $input) {
      id
      businessId
      roomId
      bedLabel
      gender
      isOccupied
    }
  }
`;

// Update Bed
export const UPDATE_BED = gql`
  mutation UpdateBed($id: ID!, $input: BedInput!) {
    updateBed(id: $id, input: $input) {
      id
      businessId
      roomId
      bedLabel
      gender
      isOccupied
    }
  }
`;

// Delete Bed
export const DELETE_BED = gql`
  mutation DeleteBed($id: ID!) {
    deleteBed(id: $id)
  }
`;


export const CREATE_REFERRAL_LINK = gql`
  mutation CreateReferralLink($businessId: Int!, $expiresInDays: Int) {
    createReferralLink(businessId: $businessId, expiresInDays: $expiresInDays) {
      token
      expiresAt
    }
  }
`;

export const UPDATE_REQUEST_STATUS = gql`
  mutation UpdateRequestStatus($id: ID!, $status: InquiryStatus!, $note: String) {
    updateRequestStatus(id: $id, status: $status, note: $note) {
      success
      id
      message
    }
  }
`;

export const RESERVE_REQUEST_BEDS = gql`
  mutation ReserveRequestBeds(
    $id: ID!
    $bedIds: [ID!]!
    $feePerBed: Float!
    $plan: InstallmentPlan!
    $total: Float!
    $amountDueNow: Float!
  ) {
    reserveRequestBeds(
      id: $id
      bedIds: $bedIds
      feePerBed: $feePerBed
      plan: $plan
      total: $total
      amountDueNow: $amountDueNow
    ) {
      success
      message
      amount          # this is what you should charge now (first installment)
      reservedBedIds
    }
  }
`;


export const CONFIRM_REQUEST_SETTLEMENT = gql`
  mutation ConfirmRequestSettlement(
    $id: ID!, 
    $paypalCaptureId: String!
  ) {
    confirmRequestSettlement(
      id: $id, 
      paypalCaptureId: $paypalCaptureId
    ) {
      success
      message
      amount
      referralLead { id status }
      updatedBeds { id isOccupied }
    }
  }
`;

export const VACATE_BEDS = gql`
  mutation VacateBeds($businessId: Int!, $bedIds: [ID!]!, $note: String) {
    vacateBeds(businessId: $businessId, bedIds: $bedIds, note: $note) {
      success
      count
      message
        beds {
      id
      bedLabel
      isOccupied
      updatedAt
    }
    }
  }
`;

export const ACCEPT_MOVEIN_AND_CHARGE = gql`
  mutation AcceptMoveInAndCharge($businessId: ID!, $placementId: ID!, $mode: PlacementMode, $isRush: Boolean) {
    acceptMoveInAndCharge(businessId: $businessId, placementId: $placementId, mode: $mode, isRush: $isRush) {
      currency
      baseCents
      rushCents
      totalCents
      mode
      isRush
    }
  }
`;

export const CREATE = gql`
  mutation CreateInvoice($bizId: Int!, $placementId: Int, $mode: String!, $isRush: Boolean!, $memo: String) {
    createPlacementInvoice(
      businessId: $bizId,
      placementId: $placementId,
      mode: $mode,
      isRush: $isRush,
      memo: $memo
    ) {
      id
      totalCents
      status
    }
  }
`;


export const ACCEPT_PROVIDER_AGREEMENT = gql`
  mutation AcceptProviderAgreement($businessId: Int!) {
    acceptProviderAgreement(businessId: $businessId) {
      success
      acceptedAt
      agreementVersion
    }
  }
`;
export const ACCEPT_PROVIDER_AGREEMENT_FOR_LEAD = gql`
  mutation AcceptProviderAgreementForLead($input: AcceptProviderAgreementForLeadInput!) {
    acceptProviderAgreementForLead(input: $input) {
      success
      acceptanceId
      agreementVersion
      acceptedAt  
    }
  }
`;
export const CHANGE_REFERRAL_LEAD_STATUS = gql`
  mutation ChangeReferralLeadStatus($id: ID!, $status: String!) {
    changeReferralLeadStatus(id: $id, status: $status) {
      id
      status
    }
  }
`;

export const CREATE_REQUEST = gql`
  mutation CreateRequest($input: CreateRequestInput!) {
    createRequest(input: $input) {
      success
      id
      viewerToken
      message
    }
  }
`;
export const PAY_REFERRAL_INSTALLMENT = gql`
  mutation PayReferralInstallment($installmentId: ID!, $amount: Float!, $paypalCaptureId: String!) {
    payReferralInstallment(installmentId: $installmentId, amount: $amount, paypalCaptureId: $paypalCaptureId) {
      success
      message
      installment {
        id
        status
        paidAmount
        paidAt
      }
    }
  }
`;

// Save my referrer profile
export const UPSERT_REFERRER_PROFILE = gql`
  mutation UpsertReferrerProfile($input: UpsertReferrerProfileInput!) {
    upsertReferrerProfile(input: $input) {
      success
      message
      referrer { id userId orgName phone }
    }
  }
`;

// Referrer-entered settlement total
export const UPDATE_REFERRAL_SETTLEMENT_BY_REFERRER = gql`
  mutation UpdateReferralSettlementByReferrer(
    $input: UpdateReferralSettlementByReferrerInput!
  ) {
    updateReferralSettlementByReferrer(input: $input) {
      id
      settlementTotalByReferrer
      updatedAt
    }
  }
`;

export const SHARE_PROVIDER_CONTACT = gql`
  mutation ShareProviderContact($leadId: ID!) {
    shareProviderContact(input: { leadId: $leadId }) {
      __typename
      id
      providerContact {
        status
        updatedAt
        emailMasked
        phoneMasked
        email
        phone
      }
    }
  }
`;
/** Claim a concierge case (sets status=ASSIGNED, ownerId=current user) */
export const CLAIM_CONCIERGE_CASE = gql`
  mutation ClaimConciergeCase($leadId: ID!) {
    claimConciergeCase(leadId: $leadId) {
      success
      message
      concierge { status ownerAgentId ownerAgentName slaAt currentOwnerAssignedAt currentOwnerAssignedByUserId }
    }
  }
`;

export const TRANSFER_CONCIERGE_CASE = gql`
  mutation TransferConciergeCase($leadId: ID!, $targetAgentId: ID!, $note: String) {
    transferConciergeCase(leadId: $leadId, targetAgentId: $targetAgentId, note: $note) {
      success
      message
      concierge { status ownerAgentId ownerAgentName slaAt currentOwnerAssignedAt currentOwnerAssignedByUserId }
    }
  }
`;

export const REASSIGN_CONCIERGE_CASE = gql`
  mutation ReassignConciergeCase($leadId: ID!, $targetAgentId: ID!, $note: String) {
    reassignConciergeCase(leadId: $leadId, targetAgentId: $targetAgentId, note: $note) {
      success
      message
      concierge { status ownerAgentId ownerAgentName slaAt currentOwnerAssignedAt currentOwnerAssignedByUserId }
    }
  }
`;

/** Mark concierge intake completed (status=INTAKE_DONE) */
export const COMPLETE_CONCIERGE_INTAKE = gql`
  mutation CompleteConciergeIntake($leadId: ID!, $notes: String) {
    completeConciergeIntake(leadId: $leadId, notes: $notes) {
      success
      message
      concierge {
        status
        ownerAgentId
        slaAt
      }
    }
  }
`;



export const SEND_CHAT_MESSAGE = gql`
  mutation SendChatMessage($input: SendChatMessageInput!) {
    sendChatMessage(input: $input) {
      success
      message
      row {
        id
        leadId
        channel
        senderRole
        body
        createdAt
      }
    }
  }
`;

export const CHAT_MESSAGE_ADDED = gql`
  subscription ChatMessageAdded($leadId: ID!, $channel: ChatChannel!) {
    chatMessageAdded(leadId: $leadId, channel: $channel) {
      id
      leadId
      channel
      senderRole
      body
      createdAt
      __typename
    }
  }
`;

export const UPSERT_BOOKING_TYPE_REFERRER = gql`
  mutation UpsertBookingTypeReferrer($input: UpsertBookingTypeReferrerInput!) {
    upsertBookingTypeReferrer(input: $input) {
      id
      bookingTypeId
      referrerId
      isReady
      groupId 
    }
  }
`;

export const CLAIM_AND_MAP = gql`
  mutation ClaimAndMapBusiness($input: ClaimAndMapInput!) {
    claimAndMapBusiness(input: $input) {
      success
      message
      token
      businessId
      claimStatus
    }
  }
`;

export const RELEASE_BUSINESS_CLAIM = gql`
  mutation ReleaseBusinessClaim($businessId: ID!) {
    releaseBusinessClaim(businessId: $businessId) {
      success
      message
      token
      businessId
      claimStatus
    }
  }
`;


export const ENABLE_PROVIDER_AGREEMENT = gql`
  mutation EnableProviderAgreement($leadId: ID!) {
    sendProviderAgreement(leadId: $leadId) {
      success
      message
    }
  }
`;

export const UNCLAIM_CONCIERGE_CASE = gql`
  mutation UnclaimConciergeCase($leadId: ID!) {
    unclaimConciergeCase(leadId: $leadId) {
      success
      message
      concierge {
        status
        ownerAgentId
        ownerAgentName
        slaAt
        currentOwnerAssignedAt
        currentOwnerAssignedByUserId
      }
    }
  }
`;

export const UPDATE_CONCIERGE_LEAD_FOLLOW_UP = gql`
  mutation UpdateConciergeLeadFollowUp($leadId: ID!, $input: ConciergeFollowUpInput!) {
    updateConciergeLeadFollowUp(leadId: $leadId, input: $input) {
      id
      followUpStatus
      lastFollowUpAt
      nextFollowUpAt
      followUpNotes
      followUpSummary {
        followUpStatus
        lastFollowUpAt
        nextFollowUpAt
        followUpNotes
        responsibleAgentId
        responsibleAgentName
      }
    }
  }
`;

export const CLAIM_CONCIERGE_BROADCAST_GROUP = gql`
  mutation ClaimConciergeBroadcastGroup($groupId: ID!) {
    claimConciergeBroadcastGroup(groupId: $groupId) {
      success
      message
      concierge {
        status
        ownerAgentId
        ownerAgentName
        slaAt
        currentOwnerAssignedAt
        currentOwnerAssignedByUserId
      }
    }
  }
`;

export const TRANSFER_CONCIERGE_BROADCAST_GROUP = gql`
  mutation TransferConciergeBroadcastGroup($groupId: ID!, $targetAgentId: ID!, $note: String) {
    transferConciergeBroadcastGroup(groupId: $groupId, targetAgentId: $targetAgentId, note: $note) {
      success
      message
      concierge {
        status
        ownerAgentId
        ownerAgentName
        slaAt
        currentOwnerAssignedAt
        currentOwnerAssignedByUserId
      }
    }
  }
`;

export const REASSIGN_CONCIERGE_BROADCAST_GROUP = gql`
  mutation ReassignConciergeBroadcastGroup($groupId: ID!, $targetAgentId: ID!, $note: String) {
    reassignConciergeBroadcastGroup(groupId: $groupId, targetAgentId: $targetAgentId, note: $note) {
      success
      message
      concierge {
        status
        ownerAgentId
        ownerAgentName
        slaAt
        currentOwnerAssignedAt
        currentOwnerAssignedByUserId
      }
    }
  }
`;

export const UNCLAIM_CONCIERGE_BROADCAST_GROUP = gql`
  mutation UnclaimConciergeBroadcastGroup($groupId: ID!) {
    unclaimConciergeBroadcastGroup(groupId: $groupId) {
      success
      message
      concierge {
        status
        ownerAgentId
        ownerAgentName
        slaAt
        currentOwnerAssignedAt
        currentOwnerAssignedByUserId
      }
    }
  }
`;

export const UPDATE_CONCIERGE_BROADCAST_GROUP_FOLLOW_UP = gql`
  mutation UpdateConciergeBroadcastGroupFollowUp($groupId: ID!, $input: ConciergeFollowUpInput!) {
    updateConciergeBroadcastGroupFollowUp(groupId: $groupId, input: $input) {
      id
      followUpStatus
      lastFollowUpAt
      nextFollowUpAt
      followUpNotes
      followUpSummary {
        followUpStatus
        lastFollowUpAt
        nextFollowUpAt
        followUpNotes
        responsibleAgentId
        responsibleAgentName
      }
    }
  }
`;

export const SEND_CONCIERGE_BROADCAST_INVITES = gql`
  mutation SendConciergeBroadcastInvites($groupId: ID!, $businessIds: [ID!]) {
    sendConciergeBroadcastInvites(groupId: $groupId, businessIds: $businessIds) {
      id
      winnerInviteId
      invites {
        id
        businessId
        status
        respondedAt
      }
    }
  }
`;

export const ADD_BUSINESSES_TO_CONCIERGE_BROADCAST_GROUP = gql`
  mutation AddBusinessesToConciergeBroadcastGroup($groupId: ID!, $businessIds: [ID!]!) {
    addBusinessesToConciergeBroadcastGroup(groupId: $groupId, businessIds: $businessIds) {
      id
      winnerInviteId
      invites {
        id
        businessId
        status
        respondedAt
      }
    }
  }
`;

export const RESEND_CONCIERGE_BROADCAST_INVITE = gql`
  mutation ResendConciergeBroadcastInvite($inviteId: ID!) {
    resendConciergeBroadcastInvite(inviteId: $inviteId) {
      id
      businessId
      status
      respondedAt
    }
  }
`;

export const CREATE_REFERRAL_LEAD_BROADCAST = gql`
  mutation CreateReferralLeadBroadcast(
    $seed: ReferralSeedInput!
    $referrerId: Int
    $referralSourceTypeId: Int
    $filter: BroadcastFilterInput!
  ) {
    createReferralLeadBroadcast(
      seed: $seed
      referrerId: $referrerId
      referralSourceTypeId: $referralSourceTypeId
      filter: $filter
    ) {
      inviteCount
      group {
        id
        createdAt
        leadSourceType
        sourceAgentId
        intakeRoutingStatus
        routingMode
        assignedAgentId
        commissionAgentId
        assignmentOrigin
        invites {
          id
          status
          business { id name url }
        }
      }
    }
  }
`;


export const PROVIDER_RESPOND_INVITE = gql`
  mutation ProviderRespondInvite(
    $inviteId: ID!
    $action: InviteStatus!
    $providerNote: String
    $agreementAccepted: Boolean
  ) {
    providerRespondInvite(
      inviteId: $inviteId
      action: $action
      providerNote: $providerNote
      agreementAccepted: $agreementAccepted
    ) {
      id
      status
      respondedAt
      providerNote
    }
  }
`;

export const PICK_BROADCAST_WINNER = gql`
  mutation PickBroadcastWinner($groupId: ID!, $inviteId: ID!) {
    pickBroadcastWinner(groupId: $groupId, inviteId: $inviteId) {
      id
      winnerInviteId
      winnerLeadId
      invites { id status business { id name url } }
    }
  }
`;

export const SET_REFERRAL_BROADCAST_GROUP_CLOSED = gql`
  mutation SetReferralBroadcastGroupClosed($groupId: ID!, $closed: Boolean!) {
    setReferralBroadcastGroupClosed(groupId: $groupId, closed: $closed) {
      id
      closedAt
      winnerInviteId
    }
  }
`;
